/**
 * SYR-107 — what a visitor watches while their brand is researched.
 *
 * A run takes about five minutes, so this has to be honest rather than decorative.
 * Two things make it honest: the stage comes from the job row (the research
 * service stamps it at each real boundary), and the queue position is a live
 * count of runs ahead. "3 runs ahead of you" explains a wait that a stalled
 * bar just looks broken through.
 *
 * Past the configured wait it stops polling and hands over to the email. The
 * email is the contract here; this panel is the bonus.
 */

import React from 'react';
import { Progress, Typography } from 'antd';
import { CheckCircleFilled, LoadingOutlined } from '@ant-design/icons';
import { FormattedMessage } from 'react-intl';
import { THEME } from '../../components/MarketerView/config/theme';

const { Text } = Typography;

// In the order the research service reports them.
const STAGES = [
    { key: 'reading_website', id: 'publicHypothesis.stage.website', label: 'Reading your website' },
    { key: 'finding_listings', id: 'publicHypothesis.stage.listings', label: 'Finding your public listings' },
    { key: 'reading_reviews', id: 'publicHypothesis.stage.reviews', label: 'Reading your customer reviews' },
    { key: 'reading_competitors', id: 'publicHypothesis.stage.competitors', label: 'Looking at your competitors' },
    { key: 'making_sense', id: 'publicHypothesis.stage.synthesis', label: 'Working out what it means' },
];

interface Props {
    brandName?: string | null;
    stage?: string | null;
    queuePosition?: number;
    elapsedSeconds?: number;
    handedOverToEmail: boolean;
}

export const ProgressPanel: React.FC<Props> = ({
    brandName,
    stage,
    queuePosition,
    elapsedSeconds,
    handedOverToEmail,
}) => {
    const currentIndex = STAGES.findIndex((s) => s.key === stage);
    // Formation happens after the last research stage, so a job past
    // 'making_sense' is genuinely almost done.
    const percent =
        currentIndex < 0 ? 5 : Math.round(((currentIndex + 1) / (STAGES.length + 1)) * 100);

    if (handedOverToEmail) {
        return (
            <div>
                <h2 style={{ fontSize: 20, fontWeight: 700, color: THEME.textBase, margin: '0 0 12px' }}>
                    <FormattedMessage
                        id="publicHypothesis.progress.slowTitle"
                        defaultMessage="This one is taking a while"
                    />
                </h2>
                <p style={{ fontSize: 15, lineHeight: 1.6, color: '#334155' }}>
                    <FormattedMessage
                        id="publicHypothesis.progress.slowBody"
                        defaultMessage="Nothing has gone wrong. Some brands have a lot of public material to read. You can close this page and we'll email you the moment it's ready."
                    />
                </p>
            </div>
        );
    }

    return (
        <div>
            <h2 style={{ fontSize: 20, fontWeight: 700, color: THEME.textBase, margin: '0 0 6px' }}>
                <FormattedMessage
                    id="publicHypothesis.progress.title"
                    defaultMessage="Working on {brand}"
                    values={{ brand: brandName || 'your brand' }}
                />
            </h2>
            <p style={{ fontSize: 14, color: THEME.textSecondary, margin: '0 0 20px' }}>
                <FormattedMessage
                    id="publicHypothesis.progress.subtitle"
                    defaultMessage="Usually about five minutes. You can close this page. We'll email you the link when it's ready."
                />
            </p>

            <Progress
                percent={percent}
                showInfo={false}
                strokeColor={THEME.primary}
                trailColor={THEME.bgChip}
            />

            <div style={{ marginTop: 20 }}>
                {STAGES.map((s, i) => {
                    const done = currentIndex > i;
                    const active = currentIndex === i;
                    return (
                        <div
                            key={s.key}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 10,
                                padding: '7px 0',
                                opacity: done || active ? 1 : 0.45,
                            }}
                        >
                            {done ? (
                                <CheckCircleFilled style={{ color: '#16a34a', fontSize: 15 }} />
                            ) : active ? (
                                <LoadingOutlined style={{ color: THEME.primary, fontSize: 15 }} />
                            ) : (
                                <span
                                    style={{
                                        width: 15,
                                        height: 15,
                                        borderRadius: '50%',
                                        border: `2px solid ${THEME.border}`,
                                        display: 'inline-block',
                                    }}
                                />
                            )}
                            <Text style={{ fontSize: 14, fontWeight: active ? 600 : 400 }}>
                                <FormattedMessage id={s.id} defaultMessage={s.label} />
                            </Text>
                        </div>
                    );
                })}
            </div>

            {/* Honest about a backlog. The runner completes one job at a time,
                so saying so beats a bar that has visibly stopped. */}
            {queuePosition && queuePosition > 0 ? (
                <p style={{ fontSize: 13, color: THEME.textSecondary, marginTop: 16 }}>
                    <FormattedMessage
                        id="publicHypothesis.progress.queue"
                        defaultMessage="{count, plural, one {# run is} other {# runs are}} ahead of yours right now."
                        values={{ count: queuePosition }}
                    />
                </p>
            ) : null}

            {elapsedSeconds && elapsedSeconds > 60 ? (
                <p style={{ fontSize: 12, color: THEME.textMuted, marginTop: 6 }}>
                    <FormattedMessage
                        id="publicHypothesis.progress.elapsed"
                        defaultMessage="{minutes, plural, one {# minute} other {# minutes}} so far"
                        values={{ minutes: Math.floor(elapsedSeconds / 60) }}
                    />
                </p>
            ) : null}
        </div>
    );
};
